import { MockWebSocket } from '../utils/mockWebSocket.js';

/**
 * 优化的WebSocket管理器
 * 集成RSM数据处理和智能回调机制
 */
export class WebSocketManager {
  constructor(config) {
    this.config = {
      reconnectInterval: 3000,
      maxReconnectAttempts: 5,
      useMock: false,
      ...config
    };
    
    this.ws = null;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.isConnected = false;
    
    // 回调函数
    this.callbacks = {
      onConnectionChange: null,    // 连接状态变化
      onRSMData: null,            // RSM数据接收
      onStatsUpdate: null,        // 统计信息更新
      onError: null               // 错误处理
    };
    
    // 统计信息
    this.stats = {
      messageCount: 0,
      participantCount: 0,
      lastMessageTime: null
    };
  }

  /**
   * 设置回调函数
   */
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * 连接WebSocket
   */
  connect() {
    try {
      this.triggerConnectionChange('connecting');
      
      // 根据配置选择WebSocket类型
      if (this.config.useMock) {
        this.ws = new MockWebSocket(this.config.url);
      } else {
        this.ws = new WebSocket(this.config.url);
      }

      this.ws.onopen = () => {
        console.log('✅ WebSocket连接成功');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.triggerConnectionChange('connected');
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event);
      };

      this.ws.onclose = () => {
        console.log('❌ WebSocket连接关闭');
        this.isConnected = false;
        this.triggerConnectionChange('disconnected');
        this.scheduleReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket错误:', error);
        this.triggerConnectionChange('error');
        this.triggerError(error);
      };

    } catch (error) {
      console.error('❌ WebSocket连接失败:', error);
      this.triggerConnectionChange('error');
      this.triggerError(error);
    }
  }

  /**
   * 处理WebSocket消息
   */
  handleMessage(event) {
    // 检查连接状态，如果已断开则忽略消息
    if (!this.isConnected || !this.ws) {
      console.warn('⚠️  收到消息但连接已断开，忽略处理');
      return;
    }
    
    try {
      const data = JSON.parse(event.data);
      
      if (data.msgType === 'rsm') {
        this.handleRSMMessage(data);
      }
    } catch (error) {
      console.error('❌ 解析消息失败:', error);
      this.triggerError(error);
    }
  }

  /**
   * 处理RSM消息
   */
  handleRSMMessage(rsmData) {
    // 双重检查连接状态
    if (!this.isConnected || !this.ws) {
      console.warn('⚠️  尝试处理RSM消息但连接已断开，跳过处理');
      return;
    }
    
    this.stats.messageCount++;
    this.stats.lastMessageTime = Date.now();
    
    const participants = rsmData.participants || [];
    this.stats.participantCount = participants.length;
    
    console.log(`📨 收到RSM消息: ${participants.length}个参与者 [连接状态: ${this.isConnected ? '已连接' : '已断开'}]`);
    
    // 只有在连接状态下才处理回调
    if (this.isConnected && this.callbacks.onRSMData) {
      this.callbacks.onRSMData(participants, rsmData.timestamp);
    }
    
    // 只有在连接状态下才更新统计信息
    if (this.isConnected && this.callbacks.onStatsUpdate) {
      this.callbacks.onStatsUpdate(this.stats);
    }
  }

  /**
   * 断开连接
   */
  disconnect() {
    console.log('🔄 正在断开WebSocket连接...');
    
    // 立即标记为未连接，阻止新的消息处理
    this.isConnected = false;
    
    // 清理重连定时器
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // 强制清理WebSocket连接
    if (this.ws) {
      try {
        // 移除所有事件监听器，防止在关闭过程中触发
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onclose = null;
        this.ws.onerror = null;
        
        // 如果是MockWebSocket，强制清理interval
        if (this.ws.interval) {
          clearInterval(this.ws.interval);
          this.ws.interval = null;
        }
        
        // 关闭连接
        if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close();
        }
        
        console.log('✅ WebSocket连接已强制关闭');
      } catch (error) {
        console.error('❌ 关闭WebSocket时出错:', error);
      }
      
      // 强制设置为null
      this.ws = null;
    }

    // 重置重连计数
    this.reconnectAttempts = 0;
    
    // 触发断开连接回调
    this.triggerConnectionChange('disconnected');
    
    console.log('✅ WebSocket管理器已完全清理');
  }

  /**
   * 完全销毁WebSocketManager实例
   * 调用此方法后，实例将无法再使用
   */
  destroy() {
    console.log('🗑️  正在销毁WebSocketManager实例...');
    
    // 先断开连接
    this.disconnect();
    
    // 清理所有回调函数
    this.callbacks = {
      onConnectionChange: null,
      onRSMData: null,
      onStatsUpdate: null,
      onError: null
    };
    
    // 重置统计信息
    this.stats = {
      messageCount: 0,
      participantCount: 0,
      lastMessageTime: null
    };
    
    // 清理配置
    this.config = null;
    
    console.log('✅ WebSocketManager实例已完全销毁');
  }

  /**
   * 切换模拟模式
   */
  toggleMockMode() {
    this.disconnect();
    this.config.useMock = !this.config.useMock;
    
    // 重置统计
    this.stats.messageCount = 0;
    this.stats.participantCount = 0;
    
    setTimeout(() => {
      this.connect();
    }, 500);
    
    return this.config.useMock;
  }

  /**
   * 安排重连
   */
  scheduleReconnect() {
    if (this.reconnectAttempts < this.config.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`🔄 准备重连... (${this.reconnectAttempts}/${this.config.maxReconnectAttempts})`);

      this.reconnectTimer = setTimeout(() => {
        this.connect();
      }, this.config.reconnectInterval);
    } else {
      console.log('❌ 达到最大重连次数，停止重连');
    }
  }

  /**
   * 触发连接状态变化回调
   */
  triggerConnectionChange(status) {
    if (this.callbacks.onConnectionChange) {
      this.callbacks.onConnectionChange(status);
    }
  }

  /**
   * 触发错误回调
   */
  triggerError(error) {
    if (this.callbacks.onError) {
      this.callbacks.onError(error);
    }
  }

  /**
   * 获取连接状态
   */
  getConnectionStatus() {
    if (this.isConnected) return 'connected';
    if (this.reconnectAttempts > 0) return 'connecting';
    return 'disconnected';
  }

  /**
   * 获取统计信息
   */
  getStats() {
    return { ...this.stats };
  }
} 